import React, { Fragment, useEffect, useMemo, useState } from "react";
import Header from "../../components/header/Header";
import { Link, useParams } from "react-router-dom";
import PageTitle from "../../components/pagetitle/PageTitle";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import CtaSection from "../../components/CtaSection/CtaSection";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { useSeo } from "../../hooks/useSeo";
import Iphone15Pro from "./IphoneMockUp";
import { Safari } from "./DockType";

const staticMeta = {
  title: "Project Details | RashTech Info",
  description:
    "Explore detailed insights into our project: scope, tech stack, objectives, and outcomes delivered by RashTech Info.",
  url: "https://rashtechinfo.com/portfolio",
  image: "https://rashtechinfo.com/og-image.png",
  siteName: "RashTech Info",
  keywords:
    "project details, portfolio, web development, mobile app, software engineering, RashTech Info",
  themeColor: "#0A0D1C",
};

const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: staticMeta.siteName,
  url: "https://rashtechinfo.com/",
  logo: "https://rashtechinfo.com/logo.png",
};

const PortfolioSinglePage = () => {
  const { id } = useParams();
  const [portfolio, setPortfolio] = useState(null);
  const [project, setProject] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const decodedTitle = encodeURIComponent(id.replace(/-/g, " "));
  // console.log(decodedTitle);

  // Load SEO
  const { seo } = useSeo("/getSeo");

  // Compute dynamic SEO values (prefer project-specific info when available)
  const computed = useMemo(() => {
    const dynamic = {
      title: seo?.title,
      description: seo?.metaDescription,
      keywords: seo?.metaKeywords,
      canonicalUrl: seo?.canonicalUrl,
    };

    // If project loaded, enhance title/description for this detail page
    const projTitle = portfolio?.Project_title;
    const projDesc = portfolio?.Project_description;
    const pageTitle = projTitle
      ? `${projTitle} | Portfolio | RashTech Info`
      : dynamic.title || staticMeta.title;

    return {
      title: pageTitle,
      description: projDesc || dynamic.description || staticMeta.description,
      keywords: dynamic.keywords || staticMeta.keywords,
      url:
        dynamic.canonicalUrl ||
        (decodedTitle
          ? `https://rashtechinfo.com/portfolio_details/${decodedTitle}`
          : staticMeta.url),
      image: staticMeta.image,
      siteName: staticMeta.siteName,
      themeColor: staticMeta.themeColor,
    };
  }, [seo, portfolio, decodedTitle]);

  useEffect(() => {
    const fetchProjectsAndCategories = async () => {
      try {
        const [projectsRes, categoriesRes] = await Promise.all([
          axios.get(`https://api.rashtechinfo.com/getAllProjects`),
          axios.get(`https://api.rashtechinfo.com/getAllProjectCategories`),
        ]);
        setProject(projectsRes.data.data || []);
        // API sometimes returns categories under 'categories' or 'data'
        const cats =
          categoriesRes.data?.categories || categoriesRes.data?.data || [];
        setCategories(cats);
      } catch (error) {
        console.log(error);
      }
    };
    fetchProjectsAndCategories();
  }, []);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await fetch(`https://api.rashtechinfo.com/getTags`);
        if (!response.ok) throw new Error("Failed to fetch tags");
        const result = await response.json();
        setTags(result.findTags || []);
      } catch (error) {
        console.error("Error fetching tags:", error);
      }
    };
    fetchTags();
  }, []);

  const getCategoryName = (categoryId) => {
    const cat = categories.find((c) => c._id === categoryId);
    return cat ? cat.categoryName : "";
  };

  const getTagName = (tagId) => {
    const tag = tags.find((t) => t._id === tagId);
    return tag ? tag.tag : "";
  };

  useEffect(() => {
    const fetchProjectById = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `https://api.rashtechinfo.com/getProjectById/${decodedTitle}`
        );
        if (!response.ok) throw new Error("Failed to fetch project");
        const result = await response.json();
        setPortfolio(result.data);
      } catch (error) {
        console.error("Error fetching project:", error);
        setPortfolio(null);
      } finally {
        setLoading(false);
      }
    };
    if (decodedTitle) fetchProjectById();
  }, [decodedTitle]);

  const ClickHandler = () => {
    window.scrollTo(10, 0);
  };

  const similarProjects =
  portfolio && Array.isArray(portfolio.tags)
    ? project.filter(
        (p) =>
          p._id !== portfolio._id &&
          Array.isArray(p.tags) &&
          p.tags.some((tag) => portfolio.tags.includes(tag))
      )
    : [];

  if (loading) {
    return (
      <Fragment>
        <Header />
        <main className="page_content about-page">
          <div className="container py-5 text-center">
            <h3>Loading...</h3>
          </div>
        </main>
        <Footer />
        <Scrollbar />
      </Fragment>
    );
  }

  if (!portfolio) {
    return (
      <Fragment>
        <Header />
        <main className="page_content about-page">
          <div className="container py-5 text-center">
            <h3>Project not found.</h3>
          </div>
        </main>
        <Footer />
        <Scrollbar />
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Helmet>
        <title>{computed.title}</title>
        <meta name="description" content={computed.description} />
        {computed.keywords && (
          <meta name="keywords" content={computed.keywords} />
        )}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content={computed.themeColor} />
        {computed.url && <link rel="canonical" href={computed.url} />}

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={computed.siteName} />
        <meta property="og:title" content={computed.title} />
        <meta property="og:description" content={computed.description} />
        {computed.url && <meta property="og:url" content={computed.url} />}
        {computed.image && (
          <meta property="og:image" content={computed.image} />
        )}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={computed.title} />
        <meta name="twitter:description" content={computed.description} />
        {computed.image && (
          <meta name="twitter:image" content={computed.image} />
        )}

        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
        {/* Breadcrumbs */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://rashtechinfo.com/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Portfolio",
                item: "https://rashtechinfo.com/portfolio",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: portfolio.Project_title || "Project",
                item: computed.url,
              },
            ],
          })}
        </script>
      </Helmet>

      <Header />
      <main className="page_content about-page">
        <PageTitle
          pageTitle={portfolio.Project_title}
          pagesub={"Details"}
          pageTop={"Portfolio"}
        />
        <section className="portfolio_details_section section_space bg-light">
          <div className="container">
            <div className="details_item_image">
              <img
                src={`https://api.rashtechinfo.com/uploads/${portfolio.projectImage}`}
                alt="Portfolio Item"
              />
            </div>
            <h2 className="details_item_title">{portfolio.Project_title}</h2>
            <p>{portfolio.Project_description}</p>
            <p className="mb-0">
              providing comprehensive training and support, we leave no stone
              unturned in ensuring the success of this project. With our
              expertise and dedication, we empower our client to embrace the
              full potential of cloud computing, driving innovation.
            </p>
            <hr />
            <ul className="portfolio_details_info_list icon_list unordered_list justify-content-lg-between mb-5">
              <li>
                <span className="icon_list_text">
                  <strong className="text-dark text-uppercase">
                    Project Category:{" "}
                  </strong>
                  {getCategoryName(portfolio.projectCategory)}
                </span>
              </li>
              <li>
                <span className="icon_list_text">
                  <strong className="text-dark text-uppercase">
                    Project Purpose:{" "}
                  </strong>
                  {portfolio.projectPurpose}
                </span>
              </li>
              <li>
                <span className="icon_list_text">
                  <a href={portfolio.projectLink} target="_blank" >
                  <strong className="text-dark text-uppercase tag-chip text-white">
                  Click here
                  </strong>
                  </a>
                </span>
              </li>
            </ul>

            <h3 className="details_item_info_title pt-4">Project Tech Stack</h3>
            <div>
              {Array.isArray(portfolio.tags) && portfolio.tags.length > 0 ? (
                <ul className="tag-list unordered_list d-flex flex-wrap gap-2">
                  {portfolio.tags.map((tagId) => {
                    const tagName = getTagName(tagId);
                    return tagName ? (
                      <li key={tagId} className="tag-chip">
                        {tagName}
                      </li>
                    ) : null;
                  })}
                </ul>
              ) : (
                <span>No tags available</span>
              )}
            </div>

            <h3 className="details_item_info_title pt-4">Project Screenshot</h3>
            <div className="Project_ScreenShot">
              {portfolio.desktopViewImage && (
                <Safari
                  url={portfolio.projectLink}
                  className="macbook"
                  imageSrc={`https://api.rashtechinfo.com/uploads/${portfolio.desktopViewImage}`}
                />
              )}
              {portfolio.mobileViewImage && (
                <Iphone15Pro
                  className="iphone"
                  src={`https://api.rashtechinfo.com/uploads/${portfolio.mobileViewImage}`}
                />
              )}
            </div>

            <h3 className="details_item_info_title pt-5 mb-4">
              Our Similar Projects
            </h3>
            {similarProjects.slice(0, 2).length > 0 ? (
              <div className="row">
                {similarProjects.slice(0, 2).map((p, prj) => (
                  <div className="col-lg-6" key={prj}>
                    <div className="portfolio_block portfolio_layout_2">
                      <div className="portfolio_image">
                        <Link
                          onClick={ClickHandler}
                          className="portfolio_image_wrap bg-light"
                          to={`/portfolio/${p.Project_title.replace(
                            /\s+/g,
                            "-"
                          )}`}
                        >
                          <img
                            src={`https://api.rashtechinfo.com/uploads/${p.projectImage}`}
                            alt={p.Project_title || "Mobile App Design"}
                            style={{
                              width: "100%",
                              objectFit: "cover",
                              height: "380px",
                            }}
                          />
                        </Link>
                      </div>
                      <div className="portfolio_content">
                        <h3 className="portfolio_title">
                          <Link
                            onClick={ClickHandler}
                            to={`/portfolio/${p.Project_title.replace(
                              /\s+/g,
                              "-"
                            )}`}
                          >
                            {p.Project_title}
                          </Link>
                        </h3>
                        <ul className="category_list unordered_list">
                          <li>
                            <Link
                              onClick={ClickHandler}
                              to={`/portfolio/${p.Project_title.replace(
                                /\s+/g,
                                "-"
                              )}`}
                            >
                              <i className="fa-solid fa-tags"></i>{" "}
                              {getCategoryName(p.projectCategory)}
                            </Link>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-muted">No similar projects found.</div>
            )}
          </div>
        </section>
        <CtaSection />
      </main>
      <Footer />
      <Scrollbar />
    </Fragment>
  );
};

export default PortfolioSinglePage;
